import type { PostSlide, SlideBadge } from './build-post-slides';

/**
 * Публикация, которую заполняют руками: по ней InstaFarm статистики не дал.
 *
 * Поля держим строками — пустое поле это «не знаем», а не ноль, и таким оно
 * должно доехать до слайда. Разбор и перевод в слайд лежат здесь, отдельно
 * от React: это правила отчёта, их проверяет тест.
 */

export type ManualPost = {
  shortcode: string;
  url: string;
  /** Почему статистики нет — показываем в карточке. */
  reason: string;
  username: string;
  badge: SlideBadge;
  /** Поля ввода держим строками: пустое поле — это не ноль. */
  views: string;
  reach: string;
  likes: string;
  comments: string;
  saved: string;
  shares: string;
  reposts: string;
  profileVisits: string;
  follows: string;
  avgWatchSeconds: string;
  /** Скриншот статистики — с него читаются цифры. */
  screen: File | null;
  /** Картинка публикации — она встаёт в телефон на слайде. */
  photo: File | null;
  isReading: boolean;
};

/** Публикация в списке: собранная по ссылке или заполняемая руками. */
export type PostItem =
  | { kind: 'found'; shortcode: string; slide: PostSlide }
  | { kind: 'manual'; shortcode: string; post: ManualPost };

export function emptyManual(url: string, shortcode: string, reason: string): ManualPost {
  return {
    shortcode,
    url,
    reason,
    username: '',
    badge: 'Пост',
    views: '',
    reach: '',
    likes: '',
    comments: '',
    saved: '',
    shares: '',
    reposts: '',
    profileVisits: '',
    follows: '',
    avgWatchSeconds: '',
    screen: null,
    photo: null,
    isReading: false,
  };
}

/** «12 345» и «12,345» — одно и то же число; пустое поле — нет значения. */
export function toNumber(value: string): number | null {
  const digits = value.replace(/[\s ,]/g, '').trim();
  if (!digits) return null;
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Число из ответа модели → значение поля; null — пустое поле. */
export const text = (value: number | null): string => (value === null ? '' : String(value));

/** Ручная публикация → слайд презентации: цифры те, что в полях. */
export function manualToSlide(post: ManualPost): PostSlide {
  const video = post.badge === 'Reels';
  const avgSeconds = toNumber(post.avgWatchSeconds);
  return {
    url: post.url,
    badge: post.badge,
    username: post.username.trim().replace(/^@/, ''),
    views: toNumber(post.views) ?? 0,
    reach: toNumber(post.reach) ?? 0,
    likes: toNumber(post.likes) ?? 0,
    comments: toNumber(post.comments) ?? 0,
    saved: toNumber(post.saved) ?? 0,
    shares: toNumber(post.shares),
    reposts: toNumber(post.reposts),
    totalInteractions: null,
    profileVisits: video ? null : toNumber(post.profileVisits),
    follows: video ? null : toNumber(post.follows),
    avgWatchMs: video && avgSeconds !== null ? avgSeconds * 1000 : null,
    totalWatchMs: null,
    skipRate: null,
    // Картинку кладём файлом: сборщик презентации принимает и ссылку, и файл.
    imageUrl: '',
    publishedAt: null,
  };
}

/** Публикации для сборки файла: найденные как есть, ручные — по полям. */
export function slidesOf(items: PostItem[]): Array<{ slide: PostSlide; photo: File | null }> {
  return items.map((item) =>
    item.kind === 'found'
      ? { slide: item.slide, photo: null }
      : { slide: manualToSlide(item.post), photo: item.post.photo },
  );
}

